import { useCallback, useState } from 'react';
import { SupportedIsraeliIdChangeRequestDtoIn } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';

// Enough for years of a rare event; the oldest go first
const MAX_REMEMBERED = 300;

// A request that changes status is news again: waiting, then rejected, alerts twice
const keyOf = (request: SupportedIsraeliIdChangeRequestDtoIn) => `${request.id}:${request.status}`;

const read = (storageKey: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * HAT-1428 (Chaya, 20.10): the line above a supporteds board about ID correction requests is an
 * alert said once - after «הצג» or ×, it does not come back for the same requests. What stays is the
 * board's own filter chip, the dot beside the name and the counts in the menu.
 *
 * Remembered per browser: losing it (a private window, cleared storage) only shows the line again.
 */
export default function useSeenIsraeliIdChangeAlerts(scope: 'coordinator' | 'management') {
  const storageKey = `israeliIdChangeAlert.seen.${scope}`;
  const [seen, setSeen] = useState(() => new Set(read(storageKey)));

  const unseen = useCallback(
    (requests: SupportedIsraeliIdChangeRequestDtoIn[]) => requests.filter((request) => !seen.has(keyOf(request))),
    [seen]
  );

  const markSeen = useCallback((requests: SupportedIsraeliIdChangeRequestDtoIn[]) => {
    const next = [...new Set([...seen, ...requests.map(keyOf)])].slice(-MAX_REMEMBERED);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Not remembered - the line shows again on the next visit
    }
    setSeen(new Set(next));
  }, [seen, storageKey]);

  return { unseen, markSeen };
}
